import { ApiError } from '@ghp/api-client'
import { loginRequestSchema, type LoginRequest } from '@ghp/domain'
import { Button, Checkbox, Field, Input, TextLink } from '@ghp/ui'
import { createRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { Lock, LogIn, User } from 'lucide-react'
import { useId, useState } from 'react'

import { rootRoute } from '../../../app/routes/root'
import { AuthLayout } from '../components/auth-layout'
import { ensureSession, FieldAccountError, TeamSelectionError } from '../session'
import { useAuth } from '../use-auth'
import s from './login.module.css'

/** Déjà connecté — le cookie a suffi — on ne repasse pas par la connexion. */
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient, context.api.auth)
    if (session.state === 'signed_in') throw redirect({ to: '/' })
  },
})

const FIELDS = ['login', 'password'] as const

type LoginField = (typeof FIELDS)[number]

/**
 * Ce que l'écran retient d'un échec : le message, et **quels champs** sont en cause.
 *
 * ⚠️ Les deux ne se déduisent pas l'un de l'autre. La plupart des cas ci-dessous ne mettent
 * la saisie en cause pour rien ; marquer les champs invalides les peindrait en rouge et les
 * annoncerait faux au lecteur d'écran, alors qu'ils sont justes.
 */
interface LoginFailure {
  message: string
  faulty: readonly LoginField[]
}

/**
 * Ce que la personne doit comprendre, pas ce que le réseau a dit : un refus (401) n'est pas
 * une limite de tentatives (429, D3), qui n'est pas un serveur injoignable.
 *
 * Les deux premiers cas ne sont pas des échecs d'authentification du tout — le compte est bon,
 * mais c'en est un de terrain : sa session ne peut pas se tenir ici (D3), et la seule action utile
 * est d'ouvrir l'app de contrôle.
 *
 * ⚠️ **Ils se distinguent depuis #176**, où ils partageaient un message. Un agent entré dans une
 * seconde équipe se voyait répondre qu'il était un compte de terrain — vrai, mais inchangé depuis
 * la veille : rien ne disait que le serveur réclamait en fait un **choix d'équipe**, et on a
 * cherché la panne du côté du compte.
 */
function failureFor(thrown: unknown): LoginFailure {
  if (thrown instanceof TeamSelectionError) {
    return {
      message:
        'Ce compte de terrain appartient à plusieurs équipes : c’est l’application de contrôle qui demande laquelle, à sa connexion.',
      faulty: [],
    }
  }
  if (thrown instanceof FieldAccountError) {
    return {
      message:
        'Ce compte est un compte de terrain : il ouvre l’application de contrôle, pas le back-office.',
      faulty: [],
    }
  }
  if (thrown instanceof ApiError) {
    if (thrown.status === 401) {
      // Les deux champs, parce que le serveur répond la **même** chose pour un identifiant
      // inconnu et un mot de passe faux — sinon il énumérerait les comptes. On ne peut donc
      // pas désigner le fautif, et prétendre le contraire enverrait corriger le mauvais.
      return { message: 'Identifiant ou mot de passe refusé.', faulty: FIELDS }
    }
    if (thrown.status === 429) {
      return {
        message: 'Trop de tentatives — patienter quelques minutes avant de réessayer.',
        faulty: [],
      }
    }
    return {
      message: `Le serveur a répondu une erreur (${thrown.status}). Réessayer, puis prévenir l’équipe technique.`,
      faulty: [],
    }
  }
  return { message: 'Serveur injoignable — vérifier la connexion et réessayer.', faulty: [] }
}

/**
 * La saisie incomplète, arrêtée **avant** le réseau — et c'est la limite de tentatives qui le
 * justifie, pas la latence : `/auth/login` n'en accepte que cinq par minute et par adresse
 * ([D3](../../../../../docs/ARCHITECTURE.md#d3)). Cinq envois à vide et la personne est
 * verrouillée une minute sans avoir jamais tapé son mot de passe.
 *
 * ⚠️ La règle n'est pas réécrite ici : c'est `loginRequestSchema` de `@ghp/domain` qui dit ce
 * qu'est une demande de connexion recevable. Elle n'exige rien de plus qu'un caractère — pas
 * de longueur de mot de passe, pas de forme de `login` (M6 en produit plusieurs, `d.jean`
 * comme `dupont.jean2`) : une règle inventée ici finirait par refuser un compte valide, et
 * seul le serveur a de quoi trancher.
 */
function incompleteEntry(credentials: LoginRequest): LoginFailure | null {
  const parsed = loginRequestSchema.safeParse(credentials)
  if (parsed.success) return null

  const faulty = FIELDS.filter((field) =>
    parsed.error.issues.some((issue) => issue.path[0] === field),
  )

  // On ne refuse que ce qu'on sait montrer : un manquement qu'aucun champ ne porte partirait
  // au serveur plutôt que d'afficher une erreur que personne ne saurait corriger.
  if (faulty.length === 0) return null

  return { message: requiredMessage(faulty), faulty }
}

function requiredMessage(faulty: readonly LoginField[]): string {
  if (faulty.length > 1) return 'Identifiant et mot de passe obligatoires.'
  return faulty[0] === 'password' ? 'Mot de passe obligatoire.' : 'Identifiant obligatoire.'
}

/**
 * Écran de connexion du back-office — `login` + mot de passe applicatif (D1), en un seul
 * temps, sans choix d'équipe (M6). Deux panneaux comme le prototype, repris du terrain.
 *
 * Absents, délibérément : « Se connecter avec Microsoft » (D1, D11) et le pied de version
 * (rien ne l'enregistre). « Mot de passe oublié ? » mène au lien par e-mail (D3).
 *
 * « **Rester connecté** » gouverne la session depuis le 23/09 (`remember`) : cochée, comme au
 * prototype, le jeton de rafraîchissement vit sept jours (D3, M23) ; décochée — le geste d'un poste
 * partagé —, douze heures, dans un cookie sans `Max-Age` qui meurt avec le navigateur.
 */
function LoginScreen() {
  const { api } = loginRoute.useRouteContext()
  const { signIn } = useAuth(api.auth)
  const navigate = useNavigate()
  const failureId = useId()

  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [failure, setFailure] = useState<LoginFailure | null>(null)
  const rememberId = useId()
  const [remember, setRemember] = useState(true)

  function submit(): void {
    const credentials = { login, password, remember }
    const incomplete = incompleteEntry(credentials)

    setFailure(incomplete)
    if (incomplete) return

    signIn.mutate(credentials, {
      onSuccess: () => void navigate({ to: '/' }),
      onError: (thrown) => setFailure(failureFor(thrown)),
    })
  }

  /**
   * Marqué invalide **et** décrit par le message : les deux vont ensemble, sinon un lecteur
   * d'écran revenant sur un champ rouge entend « invalide » sans jamais réentendre pourquoi
   * — `role="alert"` n'annonce le message qu'une fois, à son apparition.
   */
  function faultOf(field: LoginField) {
    const atFault = failure?.faulty.includes(field) ?? false
    return { invalid: atFault, 'aria-describedby': atFault ? failureId : undefined }
  }

  return (
    <AuthLayout title="Connexion">
      <form
        className={s.form}
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <Field label="Identifiant">
          {({ id }) => (
            <Input
              id={id}
              name="login"
              autoComplete="username"
              autoCapitalize="none"
              icon={<User size={18} aria-hidden />}
              value={login}
              onValueChange={setLogin}
              {...faultOf('login')}
            />
          )}
        </Field>
        <Field label="Mot de passe">
          {({ id }) => (
            <Input
              id={id}
              name="password"
              type="password"
              autoComplete="current-password"
              icon={<Lock size={18} aria-hidden />}
              value={password}
              onValueChange={setPassword}
              {...faultOf('password')}
            />
          )}
        </Field>

        <div className={s.formRow}>
          <label className={s.remember} htmlFor={rememberId}>
            <Checkbox
              id={rememberId}
              className={s.rememberBox}
              checked={remember}
              onCheckedChange={setRemember}
            />
            Rester connecté
          </label>
          <TextLink render={<Link to="/forgot-password" />}>Mot de passe oublié ?</TextLink>
        </div>

        <Button
          tone="brand"
          type="submit"
          fullWidth
          className={s.submit}
          disabled={signIn.isPending}
        >
          <LogIn size={18} aria-hidden />
          Se connecter
        </Button>
      </form>

      {failure !== null && (
        <p id={failureId} className={s.error} role="alert">
          {failure.message}
        </p>
      )}
    </AuthLayout>
  )
}
